import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function ContemporaryFinance57() {
  const contentRef = useRef(null);
  const titleText = "Fiat Money and the Rise of Wokeism";
  const links = {
    prev: "/contemporaryfinance-56",
    index: "/index-finance",
    next: "/contemporaryfinance-58",
  };
  const questions = [
    {
      text: "1. What role do fiat funded central planners play in shaping culture under a fiat system?",
      options: [
        {
          value: "A",
          label:
            "A. They allow communities to freely decide their cultural values.",
        },
        {
          value: "B",
          label:
            "B. They indirectly guide cultural trends through their control of fiat money.",
        },
        {
          value: "C",
          label: "C. They promote traditional wisdom without interference",
        },
        {
          value: "D",
          label: "D. They prevent any influence of money on culture",
        },
      ],
      correct: "B",
    },
    {
      text: "2. How does the unlimited printing of fiat money affect moral standards in society?",
      options: [
        {
          value: "A",
          label: "A. It strengthens individual responsibility and discipline",
        },
        {
          value: "B",
          label:
            "B. It removes economic restraints, leading to cultural and moral compromise",
        },
        {
          value: "C",
          label: "C. It guarantees fairness in education and cultural values",
        },
        { value: "D", label: "D. It ensures moral values remain unchanged" },
      ],
      correct: "B",
    },
    {
      text: "3. Why does a fiat-based system often lead to the normalization of lower standards in education and culture?",
      options: [
        {
          value: "A",
          label:
            "A. Because central planners are forced to preserve high-quality outcomes",
        },
        {
          value: "B",
          label:
            "B. Because resources are allocated without accountability to real value",
        },
        {
          value: "C",
          label:
            "C. Because the people demand only superior quality at all times",
        },
        {
          value: "D",
          label:
            "D. Because culture and education are insulated from economic policies",
        },
      ],
      correct: "B",
    },
    {
      text: "4.  What is a long-term effect of fiat money on collective morality?",
      options: [
        {
          value: "A",
          label:
            "A. A consistent strengthening of family and community valuesk",
        },
        {
          value: "B",
          label:
            "B. A gradual erosion of discipline, truth-seeking, and responsibility",
        },
        {
          value: "C",
          label: "C. The complete independence of cultural life from money",
        },
        { value: "D", label: "D. A universal improvement in moral standards" },
      ],
      correct: "B",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={57}
        timerMinutes={60}
        currentLevel={4}
        unlockedBadges={7}
      />
      <p className="content" ref={contentRef}>
        Welcome to <b>Day 57</b>! In the previous lessons, we explored how fiat
        money distorts health and education, but today we turn to an even deeper
        consequence: the corruption of culture and thought itself. When central
        planners gain unchecked access to money creation, their influence
        inevitably extends beyond markets and institutions, seeping into the
        moral and intellectual fabric of society. Through fiat-financed schools,
        universities, and media platforms, entire generations are conditioned
        not to think independently, but to absorb narratives that serve
        political interests. What begins as subtle guidance soon evolves into
        ideological capture, where conformity is rewarded and critical thinking
        is punished. This lesson examines how fiat money fuels the rise of
        cultural movements detached from truth and reality, showing that the
        erosion of free thought and critical reasoning is not accidental but the
        direct result of a monetary system that funds illusions at the expense
        of integrity.
        <br />
        <br />
        <b>The Demoralization of Society Under Fiat Money</b>
        <br />
        <br />
        When societies detach their money from objective anchors like gold and
        embrace fiat currencies, the implications go far beyond economics.
        Inflation and political control over money do not only distort markets,
        but also corrode the very values that sustain civilization. One of the
        most profound effects of fiat money is demoralization, the gradual
        erosion of people’s ability to discern truth, pursue virtue, and make
        rational choices. When governments can print money at will, they also
        acquire the means to finance propaganda, fund ideological programs, and
        insert their vision of morality into schools, universities, media, and
        cultural institutions. Over decades, this kind of systemic influence
        weakens the independence of individuals and communities, replacing
        authentic moral frameworks with narratives convenient for those in
        power. The demoralization is not accidental but structural. Entire
        generations raised under fiat regimes are fed narratives that encourage
        them to distrust their own traditions, question stable truths, and
        submit their lives to the guidance of bureaucrats. This is why, even
        when presented with overwhelming evidence that inflation robs them of
        wealth or that collectivist ideologies weaken societies, large portions
        of the population shrug off reality. They have been demoralized to the
        point that facts are no longer decisive. Instead, they cling to whatever
        fashionable ideology the fiat machine sponsors. The end result is a
        populace unable to think critically, often confusing compassion with
        compliance, and moral integrity with obedience to the state-approved
        narrative.
        <br />
        <br />
        <b>The Distortion of Academia and the Cult of Victimhood</b>
        <br />
        <br />
        Education under fiat money becomes a tool not for intellectual
        development but for ideological indoctrination. Since universities and
        schools are heavily funded by government grants and student loans, both
        sustained by inflationary monetary policies, they need not serve
        students as their true customers. Instead, their allegiance is to the
        state and the ideologies that justify endless spending. In this
        environment, students are not treated as individuals striving for truth,
        but as products manufactured to conform to ideological standards. One
        visible expression of this distortion is the cult of victimhood. Class
        struggles once framed by economics have been reinvented as identity
        struggles. What began as debates about capitalists versus workers has
        morphed into endless conflicts between categories of race, gender, and
        sexuality. The obsession with pronouns, for example, is less about
        respecting individuals and more about enforcing ideological conformity.
        A person who fails to comply with new linguistic rules is branded
        intolerant, even though no violence has been committed. The irony is
        that enforcing pronoun use and censoring speech require actual violence,
        since government-backed institutions threaten jobs, fines, or even
        criminal penalties for those who dissent. This inversion of reality is
        the fruit of fiat-sponsored academia. When debate is silenced and
        dissent punished, education ceases to produce thinkers and instead
        manufactures activists, many of whom see life as an endless competition
        in the so-called Oppression Olympics. The fiat classroom no longer asks
        students to pursue wisdom; it teaches them to accumulate grievances and
        deploy them for political leverage.
        <br />
        <br />
        <b>From Economic Manipulation to Cultural Subversion</b>
        <br />
        <br />
        The same mechanisms that enable central banks to conjure trillions of
        dollars into existence also allow governments to reshape culture
        according to their agendas. Inflation gives political classes a blank
        check to fund movements, media narratives, and cultural programs that
        would collapse under market scrutiny. The idea of “safe spaces” in
        universities, the imposition of “trigger warnings,” and campaigns to
        “decolonize” curricula are examples of this cultural subversion. None of
        these could survive in a marketplace of ideas where only what is true,
        useful, or inspiring attracts voluntary support. Instead, they are
        subsidized with taxpayer funds that lose value every year through
        inflation. Wokeism thrives in this artificial hothouse. By rewarding
        ideological loyalty over intellectual merit, fiat systems elevate
        individuals not for their abilities but for their political signaling.
        Universities create departments devoted to politicized fields of study
        that produce little genuine scholarship but offer endless streams of
        activists prepared to perpetuate state ideologies. This kind of
        subversion has also infected corporate life. Companies that rely on
        fiat-financed subsidies, bailouts, or political connections parrot the
        latest cultural dogmas not because they are profitable but because they
        signal loyalty to the regime. Thus, the fiat money printer reshapes not
        only the economy but also the cultural fabric, normalizing absurdities
        such as mandatory pronoun declarations, criminalizing “misgendering,” or
        elevating feelings above objective truth. In this inverted order, value
        creation is penalized, while ideological obedience is rewarded.
        <br />
        <br />
        <b>The Path Out of Collective Delusion</b>
        <br />
        <br />
        The rise of wokeism under fiat regimes is not accidental but inevitable.
        When wealth is siphoned from the productive majority to politically
        connected elites through inflation, the need to justify this injustice
        intensifies. Woke ideologies provide that justification by keeping the
        public distracted with endless cultural battles. Instead of questioning
        why their purchasing power collapses year after year, people are
        encouraged to argue about microaggressions, pronouns, or perceived
        historical grievances. The fiat system thrives on division, because
        divided populations are easier to control. Yet, despite the overwhelming
        power of fiat-financed propaganda, the solution remains within reach.
        Individuals must reclaim their independence of thought, resist the cult
        of victimhood, and rediscover first principles. True justice cannot
        exist when obedience is elevated above truth, nor can freedom survive
        when speech is policed by state-backed ideologies. The path out requires
        both intellectual courage and practical alternatives. Just as barter
        once gave way to money, and money gave way to fiat, so too must fiat
        give way to sound money, the money that no government can print at will.
        Only then will cultural life be freed from the distortions of political
        funding, and individuals can rebuild a society rooted not in compliance
        but in reality. Until then, wokeism will remain the cultural mask of
        fiat inflation, a sign that when money becomes corrupted, truth and
        morality are soon corrupted as well.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Today's lesson showed how fiat money corrupts not only the economy but
        also the culture, by financing ideologies that weaken independent
        thought. We saw that when central planners control money, they can also
        control education, media, and social narratives. This control produces
        demoralization i.e people lose the ability to think critically and
        instead conform to fashionable ideologies. Academia, funded by fiat
        inflation, no longer pursues truth but manufactures activists who thrive
        on victimhood and identity conflicts. Cultural programs such as safe
        spaces, pronoun enforcement etc survive only because of fiat subsidies.
        The result is a society where ideological obedience is rewarded more
        than merit or productivity. The lesson ended by stressing that wokeism
        is not an accident, but the cultural mask of fiat money. Only sound
        money, outside the reach of government manipulation, can restore truth,
        freedom, and authentic morality.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default ContemporaryFinance57;
